<template>
  <b-table class="box" :data="listaProductos" mobile-cards>
    <b-table-column field="codigo" label="Código" v-slot="props">
      <span class="is-hidden-touch">{{ props.row.codigo }}</span>
    </b-table-column>

    <b-table-column field="nombre" label="Nombre" v-slot="props">
      <div>{{ props.row.nombre }}</div>
      <!-- Vinculaciones: este producto se vende al detal y sus envases se abren solos -->
      <div
        v-if="tienePaquetes(props.row) && !props.row.paquetes_de"
        class="is-size-7 has-text-info"
        style="line-height: 1.35; margin-top: 2px"
      >
        <div v-for="paq in props.row.paquetes" :key="paq.id_paquete">
          Producto empaquetado: <strong>{{ paq.nombre_paquete }}</strong>
          <span class="has-text-grey">
            · 1 = {{ unidadesPorEnvaseDe(paq) }} {{ props.row.unidad }} ·
            {{ paq.existencia_paquete }} en stock
          </span>
          <span
            v-if="!puedeAbrirEnvase(paq)"
            class="has-text-danger"
          >
            — vinculación inválida: no puede reponer unidades, corrígela en el
            producto
          </span>
        </div>
      </div>

      <!-- Este producto es el ENVASE de otro: su stock se consume al abrir -->
      <div
        v-if="props.row.paquetes_de"
        class="is-size-7"
        :class="vinculacionInvalida(props.row) ? 'has-text-danger' : 'has-text-grey'"
        style="line-height: 1.35; margin-top: 2px"
      >
        Es producto empaquetado de
        <strong>{{ props.row.paquetes_de.nombre_base }}</strong>
        <span v-if="vinculacionInvalida(props.row)">
          — vinculación inválida: 1 {{ props.row.nombre }} rinde
          {{ unidadesPorEnvaseDe(props.row.paquetes_de) }}
          {{ props.row.paquetes_de.unidad_base || 'unidad' }}. Corrígela en
          Inventario → editar producto → Productos empaquetados.
        </span>
        <span v-else>
          · 1 {{ props.row.nombre }} =
          {{ unidadesPorEnvaseDe(props.row.paquetes_de) }}
          {{ props.row.paquetes_de.unidad_base || 'unidad' }}
        </span>
      </div>
      <!-- Desglose del stock: unidades al detal + lo que aportan los envases − lo que consume el detalle vendido -->
      <div
        v-if="desgloseVisible(props.row)"
        class="is-size-7"
        :class="desgloseClase(props.row)"
        style="line-height: 1.35; margin-top: 1px"
      >
        Quedan:
        <strong>{{ redondearCantidad(stockRestante(props.row)) }}</strong>
        <span class="has-text-grey">
          (de {{ redondearCantidad(desgloseStock(props.row).total) }} disponibles =
          {{ redondearCantidad(desgloseStock(props.row).base) }} del producto detal
          <template v-if="desgloseStock(props.row).desdePaquetes > 0">
            + {{ redondearCantidad(desgloseStock(props.row).desdePaquetes) }} de
            productos empaquetados
          </template>
        </span>
        <span v-if="desgloseStock(props.row).cruzado > 0" class="has-text-danger">
          — {{ redondearCantidad(desgloseStock(props.row).cruzado) }} consumidos por
          {{ desgloseStock(props.row).nombreBase }})
        </span>
        <span v-else class="has-text-grey">)</span>
      </div>
    </b-table-column>

    <b-table-column field="precio" label="Precio" v-slot="props">
      <span v-if="props.row.mayoreoAplicado">
        ${{ formatoMonto(props.row.precio) }}
      </span>
      <b-select
        class="wide"
        icon="tag-multiple"
        v-model="props.row.precio"
        required
        @input="$emit('precioCambiado')"
        v-if="!props.row.mayoreoAplicado"
      >
        <option
          v-for="precio in precios(props.row)"
          :key="precio"
          :value="precio"
        >
          {{ formatoMonto(precio) }}
        </option>
      </b-select>
    </b-table-column>

    <b-table-column field="cantidad" label="Cantidad" v-slot="props">
      <div>
        <div
          class="cantidad-col"
          style="
            display: flex;
            align-items: center;
            gap: 6px;
            white-space: nowrap;
          "
        >
          <b-field class="mb-0" style="min-width: 6em">
            <b-input
              type="number"
              @input="aumentar(props.row)"
              min="0"
              step="0.01"
              v-model="props.row.cantidad"
              style="width: 6em"
              size="is-small"
            >
            </b-input>
          </b-field>
          <b-select
            v-if="tieneConversiones(props.row)"
            v-model="props.row.conversionSel"
            @input="onCambioUnidad($event, props.row)"
            size="is-small"
          >
            <option :value="-1">{{ props.row.unidad }}</option>
            <option
              v-for="(conv, idx) in props.row.conversiones"
              :key="conv.id"
              :value="idx"
            >
              {{ conv.unidad }}
            </option>
          </b-select>
          <span v-else class="is-size-7 has-text-grey" style="min-width: 3em">
            {{
              (unidades[props.row.unidad] || props.row.unidad)
                .substring(0, 6)
                .toLowerCase()
            }}
          </span>
        </div>
        <!-- Apertura automática: cuántos envases se abrirán y cuántas unidades suman.
             Se calcula con unidades por envase (cantidad_base / cantidad_paquete) y con
             el stock real de cada envase: la cuenta anterior dividía por cantidad_base y
             no mostraba nada cuando un envase rendía varias unidades. -->
        <div
          v-if="aperturaAutomatica(props.row)"
          class="is-size-7 has-text-warning"
          style="line-height: 1.3; margin-top: 2px"
        >
          Se abrirá automáticamente {{ aperturaAutomatica(props.row) }} para completar la
          venta
        </div>
      </div>
    </b-table-column>

    <b-table-column field="subtotal" label="Subtotal" v-slot="props">
      <b>${{ formatoMonto(props.row.precio * props.row.cantidad) }}</b>
    </b-table-column>

    <b-table-column field="quitar" label="Quitar" v-slot="props">
      <b-button
        icon-left="delete"
        type="is-danger"
        @click="quitar(props.row.id)"
      >
        Quitar
      </b-button>
    </b-table-column>
  </b-table>
</template>
<script>
import { UNIDADES } from "@/consts";
import Utiles from "../../Servicios/Utiles";

export default {
  name: "TablaProductos",
  props: ["listaProductos"],

  data: () => ({
    unidades: UNIDADES,
  }),

  methods: {
    quitar(id) {
      this.$emit("quitar", id);
    },

    aumentar(producto) {
      this.$emit("aumentar", producto);
    },

    precios(producto) {
      const {
        precioVenta,
        precioVenta2,
        precioVenta3,
        precioVenta4,
        precioVenta5,
        precioVenta6,
        precioVenta7,
      } = producto;
      return [
        precioVenta,
        precioVenta2,
        precioVenta3,
        precioVenta4,
        precioVenta5,
        precioVenta6,
        precioVenta7,
      ].filter((precio) => !!Number(precio));
    },

    formatoMonto(valor) {
      return Utiles.formatoMonto(valor);
    },

    redondearCantidad(valor) {
      return Utiles.redondearCantidad(valor);
    },

    precioBase(producto) {
      return producto.precioVenta;
    },

    tieneConversiones(producto) {
      return (
        Array.isArray(producto.conversiones) && producto.conversiones.length > 0
      );
    },

    tienePaquetes(producto) {
      return Array.isArray(producto.paquetes) && producto.paquetes.length > 0;
    },

    totalPaqueteStock(producto) {
      if (!producto.paquetes) return 0;
      return producto.paquetes.reduce((sum, p) => {
        return sum + (parseFloat(p.existencia_paquete) || 0);
      }, 0);
    },

    /** Unidades del detalle que rinde UN envase (cantidad_base / cantidad_paquete). */
    unidadesPorEnvaseDe(paquete) {
      return Utiles.unidadesPorEnvase(paquete);
    },

    /** Solo se puede abrir un envase que rinda 1 unidad o más del detalle. */
    puedeAbrirEnvase(paquete) {
      return Utiles.puedeAbrirEnvase(paquete);
    },

    /** True cuando la vinculación no puede rendir ni una unidad: 1 envase < 1 unidad. */
    vinculacionInvalida(producto) {
      if (!producto.paquetes_de) return false;
      return !Utiles.puedeAbrirEnvase(producto.paquetes_de);
    },

    /** Convert package stock to base units (e.g. 5 mangas × 10 = 50 cigarros) */
    totalPaqueteConvertido(producto) {
      return Utiles.unidadesDesdeEnvases(producto);
    },

    /**
     * Envases que se abrirán automáticamente con la cantidad actual del carrito.
     * Devuelve null si la cantidad no excede el stock al detal.
     */
    aperturaAutomatica(producto) {
      if (producto.paquetes_de || !this.tienePaquetes(producto)) return null;

      const cantidad = parseFloat(producto.cantidad) || 0;
      const base = parseFloat(producto.existencia) || 0;
      let faltante = cantidad - base;

      if (faltante <= 0) return null;

      const partes = [];

      for (const paquete of producto.paquetes) {
        if (faltante <= 0) break;

        const unidades = this.unidadesPorEnvaseDe(paquete);
        const stock = parseFloat(paquete.existencia_paquete) || 0;

        // Un envase inválido o sin stock no puede cubrir el faltante.
        if (unidades < 1 || stock <= 0) continue;

        const necesarios = Math.ceil(faltante / unidades);
        const aUsar = Math.min(necesarios, stock);

        if (aUsar <= 0) continue;

        // Etiqueta del cliente: lo que se abre es el "producto empaquetado".
        const etiqueta = aUsar === 1 ? 'producto empaquetado' : 'productos empaquetados'

        partes.push(
          `${aUsar} ${etiqueta} ${paquete.nombre_paquete} (+${Utiles.redondearCantidad(
            aUsar * unidades
          )} unid)`
        );
        faltante -= aUsar * unidades;
      }

      return partes.length > 0 ? "(" + partes.join(" y ") + ")" : null;
    },

    /** Total effective stock = base stock + converted package stock.
     *  Only includes sub-package stock if this product is a BASE (no paquetes_de).
     *  If this product is itself a package (tiene paquetes_de) and the base
     *  product is also in the sale, subtract what the base would consume. */
    existenciaMaxima(producto) {
      const base = parseFloat(producto.existencia) || 0;
      const conEnvases = base + this.totalPaqueteConvertido(producto);
      const consumido = Utiles.envasesConsumidosPorDetalle(
        producto,
        this.listaProductos
      );

      return Math.max(0, conEnvases - consumido);
    },

    /** Stock RESTANTE = existenciaMaxima - cantidad actual en carrito.
     *  Refleja cuántas unidades disponibles quedan realmente. */
    stockRestante(producto) {
      const maximo = this.existenciaMaxima(producto);
      const cantidad = parseFloat(producto.cantidad) || 0;
      return Math.max(0, maximo - cantidad);
    },

    /** Alias for external use (RealizarVenta calls this via the row object) */
    existenciaEfectiva(producto) {
      return this.existenciaMaxima(producto);
    },

    /** Calculate how many units come from base stock vs packages for the current quantity */
    origenStock(producto) {
      let cant = parseFloat(producto.cantidad) || 0;
      let base = parseFloat(producto.existencia) || 0;
      let desdeBase = Math.min(cant, base);
      let desdePaquete = Math.max(0, cant - base);
      return { desdeBase, desdePaquete };
    },

    /** Desglose de stock: base, paquetes, deducción cruzada y total efectivo */
    desgloseStock(producto) {
      const base = parseFloat(producto.existencia) || 0;

      // Los envases con vinculación inválida ni aportan ni consumen: el backend no puede
      // abrirlos, así que contarlos como stock disponible sería mentir al operador.
      const desdePaquetes = this.totalPaqueteConvertido(producto);
      const cruzado = Utiles.envasesConsumidosPorDetalle(
        producto,
        this.listaProductos
      );
      const nombreBase = producto.paquetes_de
        ? producto.paquetes_de.nombre_base || "producto base"
        : "";

      return {
        base,
        desdePaquetes,
        cruzado,
        nombreBase,
        total: Math.max(0, base + desdePaquetes - cruzado),
      };
    },

    /** Hay algo que explicar: envases que aportan unidades o deducción cruzada. */
    desgloseVisible(producto) {
      const desglose = this.desgloseStock(producto);
      return desglose.desdePaquetes > 0 || desglose.cruzado > 0;
    },

    /** Rojo cuando la vinculación resta stock, verde cuando lo suma. */
    desgloseClase(producto) {
      return this.desgloseStock(producto).cruzado > 0
        ? "has-text-danger"
        : "has-text-success";
    },

    onCambioUnidad(idx, producto) {
      if (idx === -1) {
        producto.precio = producto.precioVenta;
        producto.factorConversion = 1;
        producto.unidadSel = producto.unidad;
      } else {
        const conv = producto.conversiones[idx];
        producto.precio = parseFloat(conv.precio);
        producto.factorConversion = parseFloat(conv.factor);
        producto.unidadSel = conv.unidad;
      }
      this.$emit("precioCambiado");
    },
  },
};
</script>

<style scoped>
  /* Vista de tarjetas de Buefy en pantallas táctiles/móviles */
  @media (max-width: 767px) {
    ::v-deep .table-mobile-card {
      border: 1px solid #dbdbdb;
      border-radius: 8px;
      margin-bottom: 12px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
      padding: 10px 12px;
    }

    ::v-deep .table-mobile-card .field {
      margin-bottom: 8px;
      font-size: 14px;
    }

    ::v-deep .table-mobile-card .field > .label {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: #7a7a7a;
      margin-bottom: 4px;
    }

    ::v-deep .table-mobile-card .field > .control {
      font-size: 15px;
      line-height: 1.3;
    }

    ::v-deep .table-mobile-card .field .button {
      width: 100%;
    }

    ::v-deep .table-mobile-card .field .select,
    ::v-deep .table-mobile-card .field .select select {
      width: 100%;
    }

    .cantidad-col {
      flex-wrap: wrap;
      white-space: normal !important;
      gap: 8px !important;
    }

    .cantidad-col .control {
      min-width: 5.5em;
    }
  }
</style>
